/**
 * sirus/SocialPanel — R232q21 Phase 4.
 *
 * Sirus 3.3.5a-style social window. Toggled via the `O` micro-button (panel
 * flag `social`). Four tabs:
 *   – Друзья (8 mock entries: name/level/class/zone/online dot + add/remove)
 *   – Гильдия (banner placeholder, motd, member list, your rank)
 *   – Игнор (simple list with «Удалить»)
 *   – Кто рядом (5 players currently in the same zone)
 *
 * Pure mock UI — no realmClient calls. When social-store lands later this
 * panel will swap mock arrays for real subscriptions but the JSX shape will
 * stay the same.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Types + mock data
 * ──────────────────────────────────────────────────────────────── */

type TabId = 'friends' | 'guild' | 'ignore' | 'nearby';

const TABS: { id: TabId; label: string }[] = [
  { id: 'friends', label: 'Друзья' },
  { id: 'guild',   label: 'Гильдия' },
  { id: 'ignore',  label: 'Игнор' },
  { id: 'nearby',  label: 'Кто рядом' },
];

interface Friend {
  id: string;
  name: string;
  level: number;
  klass: string;
  zone: string;
  online: boolean;
}

const FRIENDS: Friend[] = [
  { id: 'f1', name: 'Светобор',   level: 80, klass: 'Жрец',     zone: 'Сребровесь',     online: true },
  { id: 'f2', name: 'Громлад',    level: 78, klass: 'Воин',     zone: 'Перешеек',       online: true },
  { id: 'f3', name: 'Тенемир',    level: 80, klass: 'Разбойник',zone: 'Цитадель',       online: false },
  { id: 'f4', name: 'Лозомира',   level: 72, klass: 'Друид',    zone: 'Мирленд',        online: true },
  { id: 'f5', name: 'Зорянка',    level: 65, klass: 'Маг',      zone: 'Дикоземье',      online: false },
  { id: 'f6', name: 'Кремень',    level: 80, klass: 'Паладин',  zone: 'Игольчатые Шпили', online: true },
  { id: 'f7', name: 'Стужа',      level: 74, klass: 'Рыцарь смерти', zone: 'Сумеречная Лощина', online: false },
  { id: 'f8', name: 'Перунка',    level: 80, klass: 'Шаман',    zone: 'Каменный Коготь', online: true },
];

interface GuildMember {
  id: string;
  name: string;
  rank: string;
  level: number;
  klass: string;
  online: boolean;
}

const GUILD_MOTD = 'Вечером — рейд в Цитадель Ледяной Короны. Сбор у портала в 20:00.';
const GUILD_NAME = 'Серебряный Авангард';
const YOUR_RANK = 'Офицер';

const GUILD_MEMBERS: GuildMember[] = [
  { id: 'g1', name: 'Воевода',     rank: 'Магистр',    level: 80, klass: 'Воин',      online: true  },
  { id: 'g2', name: 'Светобор',    rank: 'Офицер',     level: 80, klass: 'Жрец',      online: true  },
  { id: 'g3', name: 'Громлад',     rank: 'Офицер',     level: 78, klass: 'Воин',      online: true  },
  { id: 'g4', name: 'Тенемир',     rank: 'Ветеран',    level: 80, klass: 'Разбойник', online: false },
  { id: 'g5', name: 'Лозомира',    rank: 'Ветеран',    level: 72, klass: 'Друид',     online: true  },
  { id: 'g6', name: 'Зорянка',     rank: 'Член',       level: 65, klass: 'Маг',       online: false },
  { id: 'g7', name: 'Кремень',     rank: 'Член',       level: 80, klass: 'Паладин',   online: true  },
  { id: 'g8', name: 'Молодец',     rank: 'Новичок',    level: 23, klass: 'Охотник',   online: true  },
];

const IGNORED: { id: string; name: string }[] = [
  { id: 'i1', name: 'Грубиян' },
  { id: 'i2', name: 'Спамер88' },
  { id: 'i3', name: 'Тролль' },
];

const NEARBY: { id: string; name: string; level: number; klass: string }[] = [
  { id: 'n1', name: 'Стрелок',   level: 76, klass: 'Охотник'  },
  { id: 'n2', name: 'Пламена',   level: 80, klass: 'Маг'      },
  { id: 'n3', name: 'Драконник', level: 78, klass: 'Воин'     },
  { id: 'n4', name: 'Берёзка',   level: 70, klass: 'Друид'    },
  { id: 'n5', name: 'Сумрак',    level: 79, klass: 'Чернокнижник' },
];

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function SocialPanel() {
  const close = () => useUiPanels.getState().set('social', false);
  const [tab, setTab] = useState<TabId>('friends');
  const [friends, setFriends] = useState<Friend[]>(FRIENDS);
  const [ignored, setIgnored] = useState(IGNORED);

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()}>
        <SirusFrame size="md" style={{ width: 560, height: 540, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Социум</span>
            <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
          </div>

          {/* Tabs */}
          <div style={tabRowStyle}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    ...tabBtnStyle,
                    background: active ? 'rgba(201,169,106,0.15)' : 'transparent',
                    color: active ? '#FFD479' : 'var(--aiwari-fg-muted, #B19B7E)',
                    borderBottomColor: active ? 'var(--aiwari-gold, #FAB300)' : 'transparent',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Body */}
          <div style={bodyStyle}>
            {tab === 'friends' && (
              <FriendsTab
                friends={friends}
                onRemove={(id) => setFriends((prev) => prev.filter((f) => f.id !== id))}
                onAdd={() => {
                  const num = friends.length + 1;
                  setFriends((prev) => [
                    ...prev,
                    { id: `f${num}`, name: `Друг${num}`, level: 1, klass: 'Воин', zone: 'Сребровесь', online: false },
                  ]);
                }}
              />
            )}
            {tab === 'guild' && <GuildTab />}
            {tab === 'ignore' && (
              <IgnoreTab
                rows={ignored}
                onRemove={(id) => setIgnored((prev) => prev.filter((r) => r.id !== id))}
              />
            )}
            {tab === 'nearby' && <NearbyTab />}
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Tabs
 * ──────────────────────────────────────────────────────────────── */

function FriendsTab({
  friends,
  onRemove,
  onAdd,
}: {
  friends: Friend[];
  onRemove: (id: string) => void;
  onAdd: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={subtitleStyle}>{friends.filter((f) => f.online).length} в сети</span>
        <button type="button" onClick={onAdd} style={primaryBtnStyle}>+ Добавить</button>
      </div>
      <div style={listStyle}>
        {friends.map((f) => (
          <div key={f.id} style={rowStyle}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: f.online ? '#7BC97B' : '#5C5C5C',
              boxShadow: f.online ? '0 0 4px rgba(123,201,123,0.7)' : undefined,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={nameStyle}>{f.name}</div>
              <div style={subStyle}>{f.level} ур. · {f.klass} · {f.zone}</div>
            </div>
            <button type="button" onClick={() => onRemove(f.id)} style={dangerBtnStyle}>Удалить</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuildTab() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      {/* Banner placeholder */}
      <div style={{
        height: 70,
        background: 'linear-gradient(135deg, #5C2A1A, #2A140F 60%, #5C4520)',
        border: '1px solid var(--aiwari-frame-edge, #5C4520)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 14px',
        gap: 14,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'radial-gradient(circle, #FFD479, #5C4520)',
          border: '2px solid #FFD479',
        }} />
        <div>
          <div style={{ ...titleStyle, fontSize: 16 }}>{GUILD_NAME}</div>
          <div style={subStyle}>Ваш ранг: {YOUR_RANK}</div>
        </div>
      </div>

      <div style={{
        padding: '8px 10px',
        background: 'rgba(11,9,7,0.5)',
        border: '1px solid var(--aiwari-frame-edge, #5C4520)',
        fontStyle: 'italic',
        color: 'var(--aiwari-fg, #E8E1DA)',
        fontSize: 13,
      }}>
        «{GUILD_MOTD}»
      </div>

      <div style={{ ...subtitleStyle, marginTop: 4 }}>Состав ({GUILD_MEMBERS.length})</div>
      <div style={listStyle}>
        {GUILD_MEMBERS.map((m) => (
          <div key={m.id} style={{ ...rowStyle, padding: '4px 8px' }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: m.online ? '#7BC97B' : '#5C5C5C',
            }} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 10 }}>
              <span style={nameStyle}>{m.name}</span>
              <span style={subStyle}>{m.level} ур. · {m.klass}</span>
            </div>
            <span style={{ ...subStyle, color: '#FFD479' }}>{m.rank}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IgnoreTab({ rows, onRemove }: { rows: { id: string; name: string }[]; onRemove: (id: string) => void }) {
  if (rows.length === 0) {
    return <div style={emptyStyle}>Список пуст.</div>;
  }
  return (
    <div style={listStyle}>
      {rows.map((r) => (
        <div key={r.id} style={rowStyle}>
          <span style={{ ...nameStyle, flex: 1 }}>{r.name}</span>
          <button type="button" onClick={() => onRemove(r.id)} style={dangerBtnStyle}>Удалить</button>
        </div>
      ))}
    </div>
  );
}

function NearbyTab() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={subtitleStyle}>Игроков рядом: {NEARBY.length}</div>
      <div style={listStyle}>
        {NEARBY.map((p) => (
          <div key={p.id} style={rowStyle}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#7BC97B' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={nameStyle}>{p.name}</div>
              <div style={subStyle}>{p.level} ур. · {p.klass}</div>
            </div>
            <button type="button" style={primaryBtnStyle} onClick={() => console.debug('[social] whisper', p.name)}>
              Написать
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 8,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 18,
  letterSpacing: 0.8,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const subtitleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  letterSpacing: 0.4,
};

const closeBtnStyle: CSSProperties = {
  width: 28, height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer',
  fontFamily: 'inherit',
};

const tabRowStyle: CSSProperties = {
  display: 'flex',
  gap: 0,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
  marginTop: 8,
};

const tabBtnStyle: CSSProperties = {
  padding: '8px 16px',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.4,
  cursor: 'pointer',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  height: 410,
  marginTop: 10,
  overflowY: 'auto',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  overflowY: 'auto',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 8px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const nameStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const subStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontStyle: 'italic',
};

const primaryBtnStyle: CSSProperties = {
  padding: '4px 12px',
  background: 'linear-gradient(180deg, #2A2014, #1F1813)',
  border: '1px solid var(--aiwari-gold-dark, #7A5A18)',
  color: 'var(--aiwari-gold-soft, #FFD479)',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 12,
  cursor: 'pointer',
};

const dangerBtnStyle: CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  border: '1px solid #5C2520',
  color: '#FF8A80',
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 11,
  cursor: 'pointer',
};

const emptyStyle: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  fontStyle: 'italic',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
};

export default SocialPanel;
